"use client";

import { faSearch } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ScreenOverlay, Typography, cn, useKeydown } from "@tosize/ui";
import {
	type ComponentProps,
	type FormEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Link } from "~/components/Link";
import { usePathnameChange } from "~/hooks/useRouteChange";
import { useRouter } from "~/hooks/useRouter";
import SearchResult from "./Result";
import useSearch from "./useSearch";

interface formProps extends ComponentProps<"form"> {
	customerSearch?: boolean | undefined;
	suggestions: { title: string; href: string }[];
	className?: string | undefined;
	locale: string;
	inputPlaceholder: string;
	suggestionsTitle: string;
	loading: string;
	noResults: string;
	color?: "light" | "dark";
}

export default function MiniSearch(props: formProps) {
	const { customerSearch, suggestions = [], className, color = "light" } = props;

	const router = useRouter();
	const { resetState, searchTextHandler, localData, searchText, loading } = useSearch(props.locale);
	const [resultsOpen, setResultsOpen] = useState(false);
	const wrapperRef = useRef<HTMLFormElement>(null);
	const searchResultOpen = useMemo(() => !!searchText && resultsOpen, [resultsOpen, searchText]);

	const closeMenuHandler = useCallback(() => {
		setResultsOpen(false);
		resetState();
	}, [resetState]);

	const onSubmitHandler = useCallback(
		async (e: FormEvent<HTMLElement>) => {
			e.preventDefault();

			if (searchText) {
				void router.push(`/search?q=${encodeURIComponent(searchText)}`);

				setResultsOpen(false);
			}
		},
		[router, searchText],
	);

	useKeydown((event) => {
		if (event.target instanceof HTMLElement) {
			event.target.blur();
		}

		return closeMenuHandler();
	});

	usePathnameChange(closeMenuHandler);

	useEffect(() => {
		function outsideFocusHandler(event: Event) {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target as Element)) {
				closeMenuHandler();
			}
		}

		document.addEventListener("mousedown", outsideFocusHandler);
		document.addEventListener("focusin", outsideFocusHandler);

		return () => {
			document.removeEventListener("mousedown", outsideFocusHandler);
			document.removeEventListener("focusin", outsideFocusHandler);
		};
	}, [closeMenuHandler]);

	return (
		<>
			{resultsOpen && (
				<ScreenOverlay variant="controlled" open={resultsOpen} className="hidden xl:inline" />
			)}

			<form
				ref={wrapperRef}
				className={cn("relative", { "!max-w-none z-dialog": resultsOpen }, className)}
				onSubmit={onSubmitHandler}
			>
				<div
					className={cn("relative flex w-full overflow-hidden shadow-sm", {
						rounded: !resultsOpen,
						"rounded-t": resultsOpen,
						"bg-white": color === "light",
						"bg-shades-light": color === "dark",
					})}
				>
					<input
						type="search"
						placeholder={props.inputPlaceholder}
						value={searchText}
						className={cn(
							"h-10 w-full appearance-none border-b-2 p-2 pr-9 text-sm leading-none placeholder:text-shades-dark focus:outline-none",
							{
								"border-transparent": !((searchResultOpen || customerSearch) && resultsOpen),
								"border-shades": resultsOpen || searchResultOpen,
								"bg-white": color === "light",
								"bg-shades-light": color === "dark",
							},
						)}
						onChange={(e) => searchTextHandler(e.target.value)}
						onClick={() => setResultsOpen(true)}
						onFocus={() => setResultsOpen(true)}
						onKeyDown={async (e) => {
							if (e.key === "ArrowDown") {
								e.preventDefault();
								const resultContainer = e.currentTarget.parentElement?.nextElementSibling;
								resultContainer?.querySelector("a")?.focus({ preventScroll: true });
							}
						}}
					/>
					<button
						type="submit"
						className="-translate-y-1/2 absolute top-1/2 right-0 min-w-[40px] bg-primary-medium p-2 text-white"
						onSubmit={onSubmitHandler}
						title={props.inputPlaceholder}
					>
						<FontAwesomeIcon icon={faSearch} fixedWidth />
					</button>
					{searchResultOpen && (
						<span className="absolute bottom-0 left-2 h-0.5 overflow-hidden text-sm leading-none">
							{searchText}
						</span>
					)}
				</div>

				{searchResultOpen ? (
					<SearchResult
						variant="mini"
						lang={props.locale}
						onKeyDown={async (e) => {
							if (e.key === "ArrowUp" || e.key === "ArrowDown") {
								e.preventDefault();

								const results = e.currentTarget.querySelectorAll("a");
								const activeIndex =
									document.activeElement instanceof HTMLAnchorElement
										? Array.from(results).indexOf(document.activeElement)
										: undefined;

								if (activeIndex === 0 && e.key === "ArrowUp") {
									return wrapperRef.current?.querySelector("input")?.focus({ preventScroll: true });
								}

								if (activeIndex === results.length - 1 && e.key === "ArrowDown") {
									return closeMenuHandler();
								}

								if (activeIndex !== undefined) {
									const newIndex = e.key === "ArrowUp" ? activeIndex - 1 : activeIndex + 1;

									return results[newIndex]?.focus({ preventScroll: true });
								}
							}
						}}
						loading={!localData && loading}
						data={localData}
						searchText={searchText}
						customerSearch={customerSearch}
						messages={{
							loading: props.loading,
							noResults: props.noResults,
						}}
						className={cn(
							"scrollbar-none absolute top-full max-h-[calc(var(--window-y,100dvh)-var(--header-y))] w-full divide-y-2 divide-shades overflow-auto rounded-b px-2 py-2 shadow-sm last:pb-0 motion-safe:transition",
							{
								"bg-white": color === "light",
								"bg-shades-light": color === "dark",
							},
						)}
					/>
				) : (
					resultsOpen && (
						<div
							className={cn(
								"absolute top-full max-h-[calc(var(--window-y,100dvh)-var(--header-y))] w-full overflow-auto rounded-b px-2 py-2 shadow-sm motion-safe:transition",
								{
									"bg-white": color === "light",
									"bg-shades-light": color === "dark",
								},
							)}
						>
							<Typography
								variant="paragraph-italic"
								color="text-shades-dark"
								size="text-sm"
								className="truncate px-1 py-1"
							>
								{props.suggestionsTitle}
							</Typography>
							{suggestions.map((suggestion, index) => (
								<Suggestions key={index} url={suggestion.href} title={suggestion.title} />
							))}
						</div>
					)
				)}
			</form>
		</>
	);
}
interface SuggestionsProps {
	url: string;
	title: string;
	className?: string;
	color?: "light" | "dark";
}

export function Suggestions({ url, title, className, color = "light" }: SuggestionsProps) {
	return (
		<Link
			href={url}
			passHref
			className={cn(
				className,
				"flex w-full items-center space-x-4 rounded px-2 py-1 text-left focus:outline-none",
				{
					"hover:bg-shades-light focus-visible:bg-shades-light": color === "light",
					"hover:bg-shades-medium-light focus-visible:bg-shades-medium-light": color === "dark",
				},
			)}
			title={title}
		>
			<Typography variant="paragraph" className="truncate">
				{title}
			</Typography>
		</Link>
	);
}
